import React, { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Button, Typography, Dialog, TextField } from '@mui/material';
import { RootState } from '../../stores/store';
import backgroundImage from '../../assets/images/pvx-home-bg.png';
import pvxLogo from '../../assets/icons/pvx-logo-full.svg';
import TranslateIcon from '@mui/icons-material/Translate';
import LanguageSelector from '../../components/SideNav/components/LanguageSelector';
import { useTranslation } from 'react-i18next';
import { useUserLogoutMutation, useUserOnboardingRequestMutation } from '@api/userDetails';
import UserAuth from '@services/auth';
import useAuth from '@hooks/useAuth';
import useUserDetails from '@hooks/api/useUserDetails';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LogoutIcon from '@mui/icons-material/Logout';
import pvxCapitalImage from '../../assets/images/pvx-capital.png';
import pvxLambdaImage from '../../assets/images/pvx-lambda.png';
import pvxDemoImage from '../../assets/images/pvx-demo.png';
import pendingCompanyAccessHeaderImg from '../../assets/images/pending-company-access-header-img.png';
import { inputFieldStyles, textFieldStyles } from '@utils/styles';
import { MAX_FEEDBACK_LENGTH } from '../Dashboard/components/Feedback/FeedbackConstants';
import { useUserOnboardingFeedbackMutation } from '@api/userDetails';
import { USER_APPROVAL_STATUS } from '@constants/constants';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const PendingCompanyAccess: FC = () => {
  const { t: translations } = useTranslation();
  const accessRequest = useSelector((state: RootState) => state?.UserAccessData?.accessRequest);
  const [languageSelectorAnchorEl, setLanguageSelectorAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  const [userLogout] = useUserLogoutMutation();
  const [userOnboardingRequest] = useUserOnboardingRequestMutation();
  const [userOnboardingFeedback] = useUserOnboardingFeedbackMutation();
  const { getUserDetails } = useUserDetails();
  const { loginUrl } = useAuth();
  const [openEnquiryDialog, setOpenEnquiryDialog] = useState(false);
  const [enquiryText, setEnquiryText] = useState('');
  const [isRequestingAccess, setIsRequestingAccess] = useState(false);

  const logoutUser = async () => {
    await userLogout({});
    UserAuth.clearTokens();

    setTimeout(() => {
      window.location.href = loginUrl;
    }, 1000);
  };

  const handleRequestAccess = async () => {
    try {
      setIsRequestingAccess(true);
      await userOnboardingRequest({}).unwrap();
      await getUserDetails();
    } catch (error) {
      console.error('Error requesting access:', error);
    } finally {
      setIsRequestingAccess(false);
    }
  };

  const handleOpenEnquiryDialog = () => {
    setOpenEnquiryDialog(true);
  };

  const handleCloseEnquiryDialog = () => {
    setOpenEnquiryDialog(false);
    setEnquiryText('');
  };

  const handleEnquiryTextChange = (e: any) => {
    if (e.target.value.length <= MAX_FEEDBACK_LENGTH) {
      setEnquiryText(e.target.value);
    }
  };

  const handleSubmitEnquiry = async () => {
    try {
      await userOnboardingFeedback({ text: enquiryText }).unwrap();
      await getUserDetails();
      handleCloseEnquiryDialog();
    } catch (error) {
      console.error('Error submitting enquiry:', error);
    }
  };

  const accessRequestData = (accessRequest?.data as any) || {};
  const companyName = accessRequestData?.companyName || '';
  const ownerEmail = accessRequestData?.ownerEmail || '';
  const workspaceDomain = accessRequestData?.userEmailDomain;

  const isCompanyCreationRequested = useMemo(() => {
    return accessRequest?.status === USER_APPROVAL_STATUS.COMPANY_CREATION_REQUESTED;
  }, [accessRequest]);

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: { xs: '0 0 auto', md: '0 0 28%' },
          minWidth: { md: 200 },
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'top',
          color: '#FFFFFF',
          padding: { xs: '1.25rem', md: '1.5rem 1.5rem 1.5rem 2rem' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <img src={pvxLogo} alt="PvX Partners" style={{ height: 33 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <TranslateIcon sx={{ color: '#FFFFFF' }} />
            <Box
              sx={{
                '& .MuiButton-root': {
                  color: '#FFFFFF',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                },
                '& .MuiButton-endIcon svg': {
                  color: '#FFFFFF',
                },
              }}
            >
              <LanguageSelector
                anchorEl={languageSelectorAnchorEl}
                setAnchorEl={setLanguageSelectorAnchorEl}
              />
            </Box>
          </Box>
        </Box>

        <Box>
          <Typography sx={{ fontSize: '1rem', opacity: 0.9, mb: '1.5rem' }}>
            {translations('access_pending.explore_pvx_partners')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
            <Box sx={{ display: 'flex', gap: '0.75rem' }}>
              <Box
                component="img"
                src={pvxCapitalImage}
                alt="PvX Capital"
                sx={{ width: 32, height: 32, mt: '0.125rem' }}
              />
              <Box>
                <Typography sx={{ fontWeight: 700, mb: '0.25rem', fontSize: '1.05rem' }}>
                  {translations('access_pending.pvx_capital')}
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', opacity: 0.85, mb: '0.5rem' }}>
                  {translations('access_pending.pvx_capital_description')}
                </Typography>
                <Button
                  variant="text"
                  endIcon={<OpenInNewIcon />}
                  onClick={() =>
                    window.open('https://pvxpartners.com/funding', '_blank', 'noopener,noreferrer')
                  }
                  sx={{
                    padding: 0,
                    minWidth: 0,
                    textTransform: 'none',
                    color: '#51B8B0',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    '&:hover': { background: 'transparent' },
                  }}
                >
                  {translations('access_pending.visit_website')}
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: '0.75rem' }}>
              <Box
                component="img"
                src={pvxLambdaImage}
                alt="PvX Lambda"
                sx={{ width: 32, height: 32, mt: '0.125rem' }}
              />
              <Box>
                <Typography sx={{ fontWeight: 700, mb: '0.25rem', fontSize: '1.05rem' }}>
                  {translations('access_pending.pvx_lambda')}
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', opacity: 0.85, mb: '0.5rem' }}>
                  {translations('access_pending.pvx_lambda_description')}
                </Typography>
                <Button
                  variant="text"
                  endIcon={<OpenInNewIcon />}
                  onClick={() =>
                    window.open('https://pvxpartners.com/lambda', '_blank', 'noopener,noreferrer')
                  }
                  sx={{
                    padding: 0,
                    minWidth: 0,
                    textTransform: 'none',
                    color: '#51B8B0',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    '&:hover': { background: 'transparent' },
                  }}
                >
                  {translations('access_pending.visit_website')}
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: '0.75rem' }}>
              <Box
                component="img"
                src={pvxDemoImage}
                alt="Interactive Demo"
                sx={{ width: 32, height: 32, mt: '0.125rem' }}
              />
              <Box>
                <Typography sx={{ fontWeight: 700, mb: '0.25rem', fontSize: '1.05rem' }}>
                  {translations('access_pending.interactive_demo')}
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', opacity: 0.85, mb: '0.5rem' }}>
                  {translations('access_pending.interactive_demo_description')}
                </Typography>
                <Button
                  variant="text"
                  endIcon={<OpenInNewIcon />}
                  onClick={() =>
                    window.open('https://pvxpartners.com/', '_blank', 'noopener,noreferrer')
                  }
                  sx={{
                    padding: 0,
                    minWidth: 0,
                    textTransform: 'none',
                    color: '#51B8B0',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    '&:hover': { background: 'transparent' },
                  }}
                >
                  {translations('access_pending.visit_website')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: { xs: '1.25rem', md: '-0.5rem' } }}>
          <Typography sx={{ fontSize: '0.95rem', opacity: 0.9 }}>
            {translations('access_pending.need_help')}{' '}
            <Box
              component="a"
              href="mailto:info@pvxpartners.com"
              sx={{
                color: '#51B8B0',
                fontWeight: 700,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {translations('access_pending.contact_support')}
            </Box>
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          background: '#FFFFFF',
          padding: { xs: '1.25rem', md: '1.5rem 2rem' },
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="text"
            onClick={logoutUser}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: '#51B8B0',
              '&:hover': { background: 'transparent' },
            }}
            startIcon={<LogoutIcon />}
          >
            {translations('common.sign_out')}
          </Button>
        </Box>

        <Box
          sx={{
            maxWidth: 720,
            width: '100%',
            margin: '0 auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Box
            component="img"
            src={pendingCompanyAccessHeaderImg}
            alt="Pending Company Access"
            sx={{ width: 72, height: 72, mb: '1.75rem' }}
          />
          <Typography
            sx={{
              fontSize: { xs: '1.65rem', sm: '2rem' },
              lineHeight: { xs: '2.1rem', sm: '2.5rem' },
              fontWeight: 700,
              color: '#162936',
              mb: '0.5rem',
            }}
          >
            {translations('access_pending.existing_workspace_found')}
            <Box component="span" sx={{ display: 'block', fontWeight: 400 }}>
              {workspaceDomain}
            </Box>
          </Typography>

          {!isCompanyCreationRequested && (
            <>
              <Typography
                sx={{
                  color: '#6C6C6C',
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  mb: '1.5rem',
                }}
              >
                {translations('access_pending.existing_workspace_message')}
              </Typography>

              <Box
                sx={{
                  width: 'min(100%, 25rem)',
                  backgroundColor: '#F0F0F160',
                  border: '1px solid #E8EAEB',
                  borderRadius: '0.75rem',
                  padding: '1rem',
                  mb: '1.5rem',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '100px 1fr', sm: '130px 1fr' },
                    rowGap: '0.75rem',
                    columnGap: '0.5rem',
                    mb: '1rem',
                  }}
                >
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#162936' }}>
                    {translations('access_pending.company')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.95rem', color: '#162936' }}>
                    {companyName || '-'}
                  </Typography>

                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#162936' }}>
                    {translations('access_pending.comapny_owner')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.95rem', color: '#162936' }}>
                    {ownerEmail || '-'}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  disableElevation
                  onClick={handleRequestAccess}
                  disabled={isRequestingAccess}
                  sx={{
                    background: '#2F736E',
                    borderRadius: '5rem',
                    padding: '0.5rem 1.25rem',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    '&:hover': { background: '#285F5B' },
                  }}
                >
                  {translations('access_pending.request_access')}
                </Button>
              </Box>
            </>
          )}
          <Box sx={{ height: '1px', background: '#E8EAEB', width: '100%', mb: '1.5rem' }} />

          <Typography
            sx={{
              color: '#162936',
              fontSize: { xs: '0.95rem', sm: '1rem' },
              lineHeight: '1.375rem',
              fontWeight: 500,
              mb: '1rem',
            }}
          >
            {translations('access_pending.new_company_request_message', { companyName })}
          </Typography>

          <Button
            variant="outlined"
            onClick={handleOpenEnquiryDialog}
            disabled={isCompanyCreationRequested}
            startIcon={
              isCompanyCreationRequested ? (
                <CheckCircleOutlineIcon sx={{ color: 'primary.main' }} />
              ) : undefined
            }
            sx={{
              width: 'fit-content',
              minWidth: '9.5rem',
              borderColor: '#162936',
              color: '#162936',
              borderRadius: '5rem',
              padding: '0.5rem 1.25rem',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.875rem',
              '&:hover': {
                borderColor: '#162936',
                background: '#F0F0F160',
              },
              '&.Mui-disabled': {
                borderColor: '#162936',
                color: '#162936',
                '& .MuiButton-startIcon': {
                  color: 'primary.main',
                },
              },
            }}
          >
            {isCompanyCreationRequested
              ? translations('access_pending.request_raised')
              : translations('access_pending.raise_a_request')}
          </Button>
        </Box>
      </Box>

      <Dialog
        open={openEnquiryDialog}
        onClose={handleCloseEnquiryDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '1rem',
            padding: '2rem',
          },
        }}
      >
        <Typography
          sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#162936',
            mb: '1.5rem',
          }}
        >
          General Enquiry
        </Typography>

        <TextField
          multiline
          minRows={8}
          sx={{
            ...textFieldStyles,
            width: '100%',
            '& .MuiInputBase-root': {
              alignItems: 'flex-start',
            },
            '& .MuiInputBase-input': {
              ...inputFieldStyles,
              maxHeight: '15rem',
              resize: 'none',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowY: 'auto !important',
              '&::-webkit-scrollbar': {
                width: '0.25rem',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#E0E0E0',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#2F736E',
                borderRadius: '4px',
              },
            },
          }}
          value={enquiryText}
          onChange={handleEnquiryTextChange}
          label="Enter your enquiry here"
          inputProps={{ maxLength: MAX_FEEDBACK_LENGTH + 1 }}
          placeholder="Enter your enquiry here"
        />

        <Box
          sx={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            mt: '1.5rem',
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCloseEnquiryDialog}
            sx={{
              borderColor: '#162936',
              color: '#162936',
              borderRadius: '5rem',
              padding: '0.5rem 1.25rem',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.875rem',
              '&:hover': {
                borderColor: '#162936',
                background: '#F0F0F160',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitEnquiry}
            disabled={!enquiryText.trim()}
            sx={{
              background: '#2F736E',
              borderRadius: '5rem',
              padding: '0.5rem 1.25rem',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.875rem',
              '&:hover': { background: '#285F5B' },
              '&:disabled': {
                background: '#E0E0E0',
                color: '#A0A0A0',
              },
            }}
          >
            Submit
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default PendingCompanyAccess;
